// M13.3 — pure-string SVG builders for the Mobile PDF page.
//
// Mirrors `MobileChart.tsx` from the dashboard but emits flat
// `<svg>...</svg>` strings with no DOM, no React, no event handlers — safe
// to call from inside a Cloudflare Worker where DocRaptor's static-HTML
// pipeline picks them up. Two builders so the PDF page can lay the donut
// next to the stacked area without round-tripping through the React layer.
//
// Device colour family (kept in sync with src/client/components/report/
// MobileChart.tsx — duplicated here so the shared/PDF builder doesn't pull
// any client deps):
//   Desktop = blurple-700  (#0E1680)
//   Mobile  = pink/coral   (#EC4899)
//   Tablet  = gray-400     (#9CA3AF)
//   Other   = gray-200     (#E5E7EB)

import { extent, max } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, stack as d3Stack, stackOffsetExpand } from "d3-shape";

export type DeviceKey = "desktop" | "mobile" | "tablet" | "other";

export interface DeviceMixDonutSlice {
  device: DeviceKey;
  sessions: number;
}

export interface DeviceMixTrendRow {
  date: string;
  device: DeviceKey;
  sessions: number;
}

export interface BuildDeviceMixOpts {
  width?: number;
  height?: number;
}

export interface BuildDeviceMixDonutOpts extends BuildDeviceMixOpts {
  /** Square viewBox edge for the donut. Defaults to 140 to match the
   *  ~120-160px compact donut spec — fits the right column of the PDF
   *  page without crowding the section header. */
  size?: number;
}

export interface BuildDeviceMixAreaOpts extends BuildDeviceMixOpts {
  granularity: "day" | "week" | "month";
}

export const DEVICE_COLOR: Record<DeviceKey, string> = {
  desktop: "#0E1680", // blurple-700 — anchor blurple
  mobile: "#EC4899", // pink-500 — coral/pink CTA family from M12
  tablet: "#9CA3AF", // gray-400
  other: "#E5E7EB", // gray-200 — quiet, lives behind the named devices
};

// Canonical render order used by the donut + legend so different
// properties always read the same Desktop → Mobile → Tablet → Other
// sequence regardless of which slice happens to be biggest.
export const DEVICE_RENDER_ORDER: ReadonlyArray<DeviceKey> = [
  "desktop",
  "mobile",
  "tablet",
  "other",
];

export const DEVICE_LABEL: Record<DeviceKey, string> = {
  desktop: "Desktop",
  mobile: "Mobile",
  tablet: "Tablet",
  other: "Other",
};

const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";

// ---------------------------------------------------------------------------
// Donut
// ---------------------------------------------------------------------------

/**
 * Build a compact donut SVG of the current period's per-device session mix.
 * Returns the inline `<svg>...</svg>` string, ready to drop into the
 * print-html template. Renders an empty-state donut when total sessions is
 * zero so the PDF page never has a blank rectangle.
 */
export function buildDeviceMixDonutSvg(
  slices: DeviceMixDonutSlice[],
  opts: BuildDeviceMixDonutOpts = {},
): string {
  const size = opts.size ?? 140;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 4;
  const innerR = outerR * 0.62;

  const totals = new Map<DeviceKey, number>();
  for (const s of slices) totals.set(s.device, (totals.get(s.device) ?? 0) + s.sessions);
  const totalSessions = Array.from(totals.values()).reduce((acc, n) => acc + n, 0);

  if (totalSessions === 0) {
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="Device mix (empty)">` +
      `<circle cx="${cx}" cy="${cy}" r="${outerR}" fill="none" stroke="${GRID_COLOR}" stroke-width="2" />` +
      `<text x="${cx}" y="${cy + 3}" font-size="9" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">No data</text>` +
      `</svg>`
    );
  }

  // Walk the canonical render order so visually adjacent slices render
  // in the same colour family on every property; skip devices with zero
  // sessions (so a Desktop-only property doesn't see a degenerate
  // hairline arc).
  let cursor = 0; // running angle, radians, clockwise from 12 o'clock
  const arcs: string[] = [];
  for (const device of DEVICE_RENDER_ORDER) {
    const sessions = totals.get(device) ?? 0;
    if (sessions <= 0) continue;
    const fraction = sessions / totalSessions;
    const sweep = fraction * 2 * Math.PI;
    arcs.push(donutSlicePath(cx, cy, innerR, outerR, cursor, cursor + sweep, DEVICE_COLOR[device]));
    cursor += sweep;
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="Device mix donut">` +
    arcs.join("") +
    `</svg>`
  );
}

/**
 * Emit one donut wedge as an SVG `<path>`. We hand-roll the arc geometry
 * (rather than calling d3-shape's `arc()` generator) because the donut
 * needs to be cheap, deterministic, and identical between the React
 * dashboard and the PDF — `arc()` returns a string with floating-point
 * jitter that varies between Node and browser builds.
 */
function donutSlicePath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number,
  fill: string,
): string {
  // Math.PI/2 offset so 0 rad = 12 o'clock; clockwise sweep.
  const a0 = startAngle - Math.PI / 2;
  const a1 = endAngle - Math.PI / 2;
  const x0Outer = cx + outerR * Math.cos(a0);
  const y0Outer = cy + outerR * Math.sin(a0);
  const x1Outer = cx + outerR * Math.cos(a1);
  const y1Outer = cy + outerR * Math.sin(a1);
  const x0Inner = cx + innerR * Math.cos(a1);
  const y0Inner = cy + innerR * Math.sin(a1);
  const x1Inner = cx + innerR * Math.cos(a0);
  const y1Inner = cy + innerR * Math.sin(a0);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

  // Full-circle special case: a single path with sweep = 2π collapses
  // to a degenerate point (start == end). Render the donut as two half-
  // arcs instead so the wedge actually paints.
  if (Math.abs(endAngle - startAngle - 2 * Math.PI) < 1e-6) {
    const xMid = cx + outerR * Math.cos(Math.PI / 2);
    const yMid = cy + outerR * Math.sin(Math.PI / 2);
    return (
      `<path d="M ${x0Outer.toFixed(3)} ${y0Outer.toFixed(3)} ` +
      `A ${outerR} ${outerR} 0 0 1 ${xMid.toFixed(3)} ${yMid.toFixed(3)} ` +
      `A ${outerR} ${outerR} 0 0 1 ${x0Outer.toFixed(3)} ${y0Outer.toFixed(3)} ` +
      `M ${cx + innerR} ${cy} ` +
      `A ${innerR} ${innerR} 0 1 0 ${cx - innerR} ${cy} ` +
      `A ${innerR} ${innerR} 0 1 0 ${cx + innerR} ${cy} Z" ` +
      `fill="${escapeAttr(fill)}" fill-rule="evenodd" />`
    );
  }

  return (
    `<path d="M ${x0Outer.toFixed(3)} ${y0Outer.toFixed(3)} ` +
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x1Outer.toFixed(3)} ${y1Outer.toFixed(3)} ` +
    `L ${x0Inner.toFixed(3)} ${y0Inner.toFixed(3)} ` +
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x1Inner.toFixed(3)} ${y1Inner.toFixed(3)} Z" ` +
    `fill="${escapeAttr(fill)}" />`
  );
}

// ---------------------------------------------------------------------------
// 100% normalised stacked area
// ---------------------------------------------------------------------------

const DEFAULT_AREA_WIDTH = 480;
const DEFAULT_AREA_HEIGHT = 220;
const AREA_MARGIN = { top: 18, right: 12, bottom: 28, left: 36 };

/**
 * Build a static 100%-normalised stacked area chart for the device-mix
 * trend. Uses the canonical Desktop / Mobile / Tablet / Other sequence so
 * the visual stack ordering matches the donut + legend.
 */
export function buildDeviceMixAreaSvg(
  rows: DeviceMixTrendRow[],
  opts: BuildDeviceMixAreaOpts,
): string {
  const width = opts.width ?? DEFAULT_AREA_WIDTH;
  const height = opts.height ?? DEFAULT_AREA_HEIGHT;
  const innerW = width - AREA_MARGIN.left - AREA_MARGIN.right;
  const innerH = height - AREA_MARGIN.top - AREA_MARGIN.bottom;

  if (rows.length === 0) {
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Device mix area (empty)">` +
      `<rect x="0" y="0" width="${width}" height="${height}" fill="#FAFAFA"></rect>` +
      `<text x="${width / 2}" y="${height / 2}" font-size="12" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">No traffic in window</text>` +
      `</svg>`
    );
  }

  // Bucket the rows into a per-date matrix in canonical device order.
  // Devices missing on a given day get 0 so d3-stack still produces a
  // continuous area band.
  const dateSet = new Set<string>();
  for (const r of rows) dateSet.add(r.date);
  const dates = Array.from(dateSet).sort();
  const dateIndex = new Map(dates.map((d, i) => [d, i]));
  const cells: Record<DeviceKey, number>[] = dates.map(() => ({
    desktop: 0,
    mobile: 0,
    tablet: 0,
    other: 0,
  }));
  for (const r of rows) {
    const idx = dateIndex.get(r.date);
    if (idx === undefined) continue;
    cells[idx][r.device] = (cells[idx][r.device] ?? 0) + r.sessions;
  }

  // Drop devices that never had any sessions across the window so the
  // legend / stack don't carry zero-width bands.
  const presentDevices: DeviceKey[] = DEVICE_RENDER_ORDER.filter((d) =>
    cells.some((row) => row[d] > 0),
  );
  if (presentDevices.length === 0) {
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Device mix area (empty)">` +
      `<rect x="0" y="0" width="${width}" height="${height}" fill="#FAFAFA"></rect>` +
      `<text x="${width / 2}" y="${height / 2}" font-size="12" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">No traffic in window</text>` +
      `</svg>`
    );
  }

  const stackGen = d3Stack<Record<DeviceKey, number>>()
    .keys(presentDevices)
    .offset(stackOffsetExpand);
  const stacked = stackGen(cells);

  const dateObjs = dates.map((d) => new Date(`${d}T00:00:00Z`));
  const [xMin, xMax] = extent(dateObjs) as [Date, Date];
  const yMaxRaw = max(stacked, (s) => max(s, (d) => d[1])) ?? 1;
  const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
  const yScale = scaleLinear().domain([0, yMaxRaw]).range([innerH, 0]);

  const areaGen = d3Area<[number, number]>()
    .x((_, i) => xScale(dateObjs[i]))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]));

  const yTickPercents = [0, 25, 50, 75, 100];
  const xTickCount = opts.granularity === "day" ? 5 : opts.granularity === "week" ? 4 : 3;
  const xTicks = xScale.ticks(xTickCount);

  const gridlines = yTickPercents
    .map((pct) => {
      const y = yScale(pct / 100);
      return (
        `<line x1="0" y1="${y}" x2="${innerW}" y2="${y}" stroke="${GRID_COLOR}" stroke-width="1" />` +
        `<text x="-8" y="${y + 3}" font-size="9" fill="${AXIS_COLOR}" text-anchor="end" font-family="inherit">${pct}%</text>`
      );
    })
    .join("");

  const xAxisTicks = xTicks
    .map((d) => {
      const x = xScale(d);
      return (
        `<line x1="${x}" y1="${innerH}" x2="${x}" y2="${innerH + 4}" stroke="${AXIS_COLOR}" stroke-width="1" />` +
        `<text x="${x}" y="${innerH + 16}" font-size="9" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">${escapeText(formatXTick(d, opts.granularity))}</text>`
      );
    })
    .join("");

  const bands = stacked
    .map((s) => {
      const tuples = s.map((p) => [p[0], p[1]] as [number, number]);
      const d = areaGen(tuples);
      if (!d) return "";
      const fill = DEVICE_COLOR[s.key as DeviceKey];
      return `<path d="${d}" fill="${escapeAttr(fill)}" stroke="#ffffff" stroke-width="0.5" opacity="0.92" />`;
    })
    .join("");

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Device mix 100% stacked area">` +
    `<g transform="translate(${AREA_MARGIN.left}, ${AREA_MARGIN.top})">` +
    gridlines +
    xAxisTicks +
    bands +
    `</g>` +
    `</svg>`
  );
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
